import { jsPDF } from "jspdf";
import { fmtDate, fmtDateTime } from "@/lib/api";
import { NAVY, TEAL, GREEN, AMBER, RED, SLATE, TEAL_LT, rs, loadImage, drawHeader, drawFooter, drawPill } from "@/lib/pdfBrand";

function drawSeal(doc, cx, cy) {
  doc.setDrawColor(...TEAL);
  doc.setLineWidth(1.6);
  doc.circle(cx, cy, 30, "S");
  doc.setLineWidth(0.7);
  doc.circle(cx, cy, 24, "S");
  doc.setTextColor(...TEAL);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(6.5);
  doc.text("ZRo Pe", cx, cy - 11, { align: "center" });
  doc.setFontSize(11);
  doc.text("VERIFIED", cx, cy + 3, { align: "center" });
  doc.setFontSize(5);
  doc.text("SECURE PAYMENT", cx, cy + 15, { align: "center" });
}

// Real, downloadable ZRo Pe payment receipt — matches approved reference design.
// Financial-safety rule: NO EMI-wise principal/interest breakup; no fabricated fields.
export async function generateReceiptPdf(p) {
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const W = doc.internal.pageSize.getWidth();
  const mascot = await loadImage("/brand/zrope-mascot.png");
  const receiptNo = p.receipt_no || "RCP" + String(p.txn_id || "").replace("TXN", "");
  drawHeader(doc, { rightTitle: "PAYMENT RECEIPT", rightSub: `Receipt No: ${receiptNo}`, mascot });

  const st = (p.status || "SUCCESS").toUpperCase();
  const color = st === "SUCCESS" ? GREEN : st === "PENDING" ? AMBER : RED;

  // status icon
  const cx = W / 2, cy = 152;
  doc.setFillColor(...color);
  doc.circle(cx, cy, 22, "F");
  doc.setDrawColor(255, 255, 255);
  doc.setLineWidth(3.2);
  doc.setLineCap("round");
  doc.setLineJoin("round");
  if (st === "SUCCESS") {
    doc.line(cx - 10, cy + 1, cx - 3, cy + 9);
    doc.line(cx - 3, cy + 9, cx + 11, cy - 9);
  } else if (st === "PENDING") {
    doc.line(cx, cy - 9, cx, cy + 2);
    doc.line(cx, cy + 2, cx + 8, cy + 6);
  } else {
    doc.line(cx - 8, cy - 8, cx + 8, cy + 8);
    doc.line(cx + 8, cy - 8, cx - 8, cy + 8);
  }

  const title = st === "SUCCESS" ? "Payment Successful" : st === "PENDING" ? "Payment Pending" : "Payment Failed";
  const sub = st === "SUCCESS" ? "Your payment has been received successfully."
    : st === "PENDING" ? "Your payment is awaiting bank confirmation." : "Your payment could not be processed.";
  doc.setTextColor(...NAVY);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(20);
  doc.text(title, cx, cy + 50, { align: "center" });
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.setTextColor(...SLATE);
  doc.text(sub, cx, cy + 69, { align: "center" });

  // details card
  const cardY = cy + 92, cardH = 172;
  doc.setDrawColor(226, 232, 240);
  doc.setLineWidth(1);
  doc.setFillColor(252, 253, 255);
  doc.roundedRect(40, cardY, W - 80, cardH, 10, 10, "FD");
  const colL = 64, colR = W / 2 + 12;
  const field = (x, y, label, value, big) => {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(...SLATE);
    doc.text(label, x, y);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(big ? 15 : 11);
    doc.setTextColor(...NAVY);
    doc.text(String(value), x, y + 18);
  };
  let ry = cardY + 30;
  field(colL, ry, "Paid Amount", rs(p.amount), true);
  field(colR, ry, "Transaction ID", p.txn_id || "-");
  ry += 42;
  field(colL, ry, "Loan Name", `${p.lender || "-"}${p.loan_type ? " — " + p.loan_type : ""}`);
  field(colR, ry, "Payment Method", (p.method || "").replace(/_/g, " ") || "-");
  ry += 42;
  field(colL, ry, "Loan Account Number", p.account_number || "-");
  field(colR, ry, "Payment Date & Time", p.created_at ? fmtDateTime(p.created_at) : "-");
  ry += 42;
  field(colL, ry, "EMI Due Date", p.emi_due_date ? fmtDate(p.emi_due_date) : "-");
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(...SLATE);
  doc.text("Payment Status", colR, ry);
  const bg = st === "SUCCESS" ? [236, 253, 245] : st === "PENDING" ? [255, 251, 235] : [254, 242, 242];
  drawPill(doc, colR, ry + 6, st, color, bg, 9);

  // thank-you + verified seal
  const tY = cardY + cardH + 16;
  doc.setDrawColor(226, 232, 240);
  doc.setFillColor(248, 250, 252);
  doc.roundedRect(40, tY, W - 80, 84, 10, 10, "FD");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.setTextColor(...NAVY);
  doc.text("Thank you for your payment!", 64, tY + 34);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(...SLATE);
  doc.text("Please keep this receipt for your records.", 64, tY + 52);
  drawSeal(doc, W - 100, tY + 42);

  // need help
  const hY = tY + 100;
  doc.setDrawColor(...TEAL_LT);
  doc.setFillColor(240, 253, 250);
  doc.roundedRect(40, hY, W - 80, 74, 10, 10, "FD");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.setTextColor(...NAVY);
  doc.text("Need Help?", 64, hY + 30);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(...SLATE);
  doc.text("We're here to assist you", 64, hY + 48);
  doc.setTextColor(...NAVY);
  doc.setFontSize(9);
  doc.text("support@zrope.in", W / 2 + 12, hY + 30);
  doc.text("www.zrope.in", W / 2 + 12, hY + 52);

  drawFooter(doc, { logo: true, line2: "Trusted. Secure. Simplifying Payments." });
  doc.save(`ZRoPe-Receipt-${p.txn_id || "payment"}.pdf`);
}
